import { addMonths, setHours } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';
import type { DirectFcfsCapacityPool } from 'src/apis/direct-fcfs/types';
import { useNow } from 'src/hooks/use-now';
import { usePageParamsFromSchema } from 'src/hooks/use-page-params-from-schema';
import { getCapacityOverviewPageParamsSchema } from 'src/pages/transport/network-points/details/capacity-overview-fcfs/schema';
import { timeZone } from 'src/utils/date-time-format';

export function useCapacityOverviewPageParams(
  capacityPools: DirectFcfsCapacityPool[]
) {
  const now = useNow();

  const { startDefault, endDefault, capacityPoolId } = useMemo(() => {
    const zonedNow = toZonedTime(now, timeZone);

    const startDefault = fromZonedTime(
      setHours(zonedNow, 0),
      timeZone
    ).toISOString();
    const endDefault = fromZonedTime(
      setHours(addMonths(zonedNow, 1), 6),
      timeZone
    ).toISOString();
    return {
      startDefault,
      endDefault,
      capacityPoolId: capacityPools.at(0)?.id ?? null,
    };
  }, [now, capacityPools]);

  return usePageParamsFromSchema(
    getCapacityOverviewPageParamsSchema({
      startDefault,
      endDefault,
      capacityPoolIdDefault: capacityPoolId,
    }),
    {
      filters: {
        runtimeIntersectionFilterStart: { label: 'Runtime' },
        runtimeIntersectionFilterEnd: { label: 'Runtime' },
        capacityPoolId: { label: 'Capacity Pool' },
      },
    }
  );
}

export type CapacityOverviewPageParams = ReturnType<
  typeof useCapacityOverviewPageParams
>;
